import { FC, useMemo, useCallback } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { DropdownFilterChipField, Text, useFormField } from '@frontend/design-system';
import { useKeywordSearchStore } from '../../../providers/keyword-search.provider/keyword-search.provider';
import { useTeamChatSelector } from '../../../providers/team-chat.provider/team-chat.provider';
import { ChatAvatar } from '../../common/chat-avatar/chat-avatar';

interface ChannelFilterProps {
  focusSearchBar: () => void;
}

export const ChannelFilter: FC<ChannelFilterProps> = ({ focusSearchBar }) => {
  const { t } = useTranslation('team-chat');
  const conversations = useTeamChatSelector((ctx) => ctx.conversations);
  const currentUser = useTeamChatSelector((ctx) => ctx.user);
  const { inConversations, setInConversations } = useKeywordSearchStore(['inConversations', 'setInConversations']);

  const fieldProps = useFormField({
    type: 'multiselect',
    placeholder: t('All Chats'),
  });

  const conversationsList = useMemo(() => {
    const dms = conversations?.dm ? conversations.dm : [];
    const groups = conversations?.groups ? conversations.groups : [];
    const allConversations = [...dms, ...groups];
    const { selected, nonSelected } = allConversations.reduce<{
      selected: typeof allConversations;
      nonSelected: typeof allConversations;
    }>(
      (acc, conversation) => {
        const isSelected = inConversations.includes(conversation.channelId);
        if (isSelected && !conversation.isHidden) {
          acc.selected.push(conversation);
        } else if (!conversation.isHidden) {
          acc.nonSelected.push(conversation);
        }
        return acc;
      },
      {
        selected: [],
        nonSelected: [],
      }
    );

    return [
      ...selected.sort((a, b) => a.name.localeCompare(b.name)),
      ...nonSelected.sort((a, b) => a.name.localeCompare(b.name)),
    ].map((conv) => ({
      name: conv.name,
      id: conv.channelId,
      isDm: conv.type === 'DM',
      members:
        conv.members && currentUser ? conv.members.filter((member) => member.userID !== currentUser?.userID) : [],
    }));
  }, [conversations?.dm, conversations?.groups, currentUser, inConversations]);

  const getCustomLabel = useCallback(() => {
    if (fieldProps.value.length === 0) {
      return t('In: All Chats');
    } else if (fieldProps.value.length === 1) {
      const convo = conversationsList.find((conv) => conv.id === fieldProps.value[0])?.name;
      return convo ? t('In: {{value}}', { value: convo }) : '';
    }
    const dmLength = conversationsList.filter((conv) => fieldProps.value.includes(conv.id) && conv.isDm).length;
    const groupLength = fieldProps.value.length - dmLength;

    let str = t('In: ');

    if (dmLength > 0) {
      str += t('{{count}} DMs', { count: dmLength });
    }
    if (groupLength > 0) {
      if (dmLength > 0) {
        str += ' & ';
      }
      str += t('{{count}} Channels', { count: groupLength });
    }

    return str;
  }, [conversationsList, t, fieldProps.value]);

  return (
    <DropdownFilterChipField
      name='keyword-search-channel-filter'
      label={t('in: All Chats')}
      getCustomLabel={getCustomLabel}
      showClearAll={false}
      showSelectAll={false}
      sortLabelsBySelection
      className='dropdownFilterChipField'
      onApply={(ctx) => {
        setInConversations(ctx.value);
        focusSearchBar();
        ctx.close();
      }}
      trackingIdBase='team-chat-2.0-keyword-search-channel-filter'
      {...fieldProps}
    >
      {conversationsList.map((convo) => {
        return (
          <DropdownFilterChipField.Option key={convo.id} value={convo.id} className='dropdownOption'>
            {!convo.isDm && (
              <span css={styles.optionPrefix}>
                <Text>#</Text>
              </span>
            )}

            {convo.isDm &&
              (convo.members.length === 1 ? (
                <ChatAvatar users={[convo.members[0]]} />
              ) : (
                <span css={styles.optionPrefix}>
                  <Text size='small' weight='bold'>
                    {convo.members.length}
                  </Text>
                </span>
              ))}

            <Text>{convo.name}</Text>
          </DropdownFilterChipField.Option>
        );
      })}
    </DropdownFilterChipField>
  );
};

const styles = {
  optionPrefix: css({
    minHeight: '24px',
    minWidth: '24px',
    border: `1.5px solid ${theme.colors.neutral70}`,
    borderRadius: theme.borderRadius.medium,
    color: theme.colors.neutral70,
    justifyContent: 'center',
    display: 'flex',
    alignItems: 'center',
    height: 'fit-content',
  }),
};
